import { EMPTY_OBJECT } from 'constants/defaults';
import { hourAsCardFactory, hourHistoryFactory } from 'factories/hours';
import { entryToMemoryFactory, groupMemoriesFactory } from 'factories/timeline';
import { isEqual } from 'lodash-es';
import { DateTime } from 'luxon';
import { getHourStates } from 'redux/modules/hourStates';
import type { Changes } from 'redux/modules/hours.types';
import { SYNC_BACKGROUND_CHANGES_KEY } from 'redux/modules/hours.types';
import { getTagEntities } from 'redux/modules/tags.selectors';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import type { MemoryToProjectRelation } from 'redux/selectors/suggestions';
import { getMemoryToProjectRelationsForSuggestionsOnCalendarDate } from 'redux/selectors/suggestions';
import { getFilter, getMemoryGroupsWithProjects } from 'redux/selectors/timeline';
import { getMemoryEntries } from 'redux/selectors/timeline.filters';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import { HOUR_SCHEMA_KEY, PROJECT_SCHEMA_KEY } from 'schema/hours';
import type { RawAudit } from 'types/audits';
import type { Hour, HourAsCard } from 'types/hour';
import type { LightProject, Project } from 'types/project';
import type { Memory, MemoryGroup } from 'types/timeline';
import { OperatorMode } from 'types/timeline';
import { enrichProjectOnHour } from 'utils/hours';
import { getProjectColor } from 'utils/project';
import { getAccountCurrency, getHasHourStatesFeature } from './accounts';
import { getPinnedProjectIdsSet } from './pins';
import { getProjectVariantMap } from './projects';
import { getUserLedUserIds } from './teams';
import { getHasEnabledTic, getUserId, getUserRoleId } from './user';

// Importing getHourDraftId from hours.draft creates circular dependency and crashes this?
const readHourDraftId = (state: IState) => state.hours.draft?.id;
const readHourEntities = (state: IState) => state.hours.entities[HOUR_SCHEMA_KEY];
const readProjectEntities = (
  state: IState
): {
  [project_id: number]: LightProject;
} => state.hours.entities[PROJECT_SCHEMA_KEY];
const readHourResult = (state: IState) => state.hours.result;

const readHourHistoryEntities = (state: IState) => state.hours.history.entities;
const readHourHistoryResult = (state: IState) => state.hours.history.result;
const readHourHistoryModal = (state: IState) => state.hours.showHourHistoryModal;
const readHourHistoryXhrStatus = (state: IState) => state.hours.xhrStatus;
const readReportSelection = (state: IState) => state.superreports.selection;

const readHourXhrStatus = (state: IState) => state.hours.xhrStatus;

export const getHoursXhrStatus = readHourXhrStatus;

export const getHourXhrUIState = createSelector(
  [readHourXhrStatus],
  status => {
    if (xhrStatusUIState(status, SYNC_BACKGROUND_CHANGES_KEY).isLoading) {
      return {
        ...xhrStatusUIState(status),
        isLoading: false,
      };
    }

    return xhrStatusUIState(status);
  },
  { memoizeOptions: { resultEqualityCheck: isEqual } }
);
export const getHourIsSaving = createSelector([getHourXhrUIState], state => state.isSaving);
export const getHourXhrError = createSelector([getHourXhrUIState], state =>
  state.errors.map(sig => sig.error).join(', ')
);

export const getHourHistoryIsLoading = createSelector([readHourHistoryXhrStatus], status => {
  const xhrState = xhrStatusUIState(status);
  return xhrState.isLoading;
});

export const getShowHourHistoryModal = readHourHistoryModal;

export const getHourHistory = createSelector(
  [readHourHistoryEntities, readHourHistoryResult],
  (entities, result) => result.map(r => entities[r])
);

export const getHourHistoryForDropdown = createSelector(
  [getHourHistory, readProjectEntities],
  (history, projectEntities) => {
    const groupedHistory: Array<{
      date: string;
      history: Array<RawAudit<Partial<Changes>>>;
    }> = [];

    history
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .forEach(h => {
        const createdAt = DateTime.fromISO(h.created_at);
        let group = groupedHistory.find(g => DateTime.fromISO(g.date).hasSame(createdAt, 'day'));
        if (!group) {
          group = { date: h.created_at, history: [] };
          groupedHistory.push(group);
        }
        group.history.push(hourHistoryFactory(h, projectEntities));
      });

    return groupedHistory.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }
);

export const getHourResult = readHourResult;
export const getHourEntities = readHourEntities;

export const getUnprocessedHours = createSelector(
  [readHourResult, getHourEntities],
  (ids, entities) => ids.map(id => entities[id])
);

export const getHourProjectEntities = readProjectEntities;

/**
 * Creates a fallback project object for deleted projects.
 * This prevents crashes when hours reference projects that have been deleted via API.
 */
function createFallbackProject(projectId: number): LightProject {
  return {
    id: projectId,
    name: 'Deleted Project',
    color: 'cccccc', // Default gray color
    active: false,
    client: {
      id: 0,
      name: '',
      color: 'cccccc',
      active: true,
    },
    updated_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
  };
}

export const getHours = createSelector(
  [getUnprocessedHours, getHourProjectEntities, getProjectVariantMap],
  (hours, projectEntities, globalProjectVariantMap): Array<Hour<Project | LightProject>> => {
    return hours.map(hour => {
      const projectId = hour.project;
      const variant = globalProjectVariantMap[projectId];
      const project =
        variant?.type === 'full'
          ? variant.project
          : (projectEntities[projectId] ?? createFallbackProject(projectId));
      return {
        ...hour,
        project,
      };
    });
  }
);

export const getHoursSummary = createSelector(
  [getUnprocessedHours, getHourProjectEntities, getProjectVariantMap],
  (hours, projectEntities, globalProjectVariantMap): Array<Hour<Project | LightProject>> => {
    return hours.map(hour => {
      const projectId = hour.project;
      const variant = globalProjectVariantMap[projectId];
      const project =
        variant?.type === 'full'
          ? variant.project
          : (projectEntities[projectId] ?? createFallbackProject(projectId));
      return {
        ...hour,
        project,
      };
    });
  }
);

/**
 * Return references to project for the entry ids on Hours
 */
export const getMemoryToProjectRelationsForHoursOnCalendarDate = createSelector(
  [getHours],
  (hours): Record<number, MemoryToProjectRelation> => {
    return hours.reduce((prev: Record<number, MemoryToProjectRelation>, hour) => {
      const entryMap = hour.entry_ids.reduce(
        (prevMappings: Record<number, MemoryToProjectRelation>, entryId) => {
          prevMappings[entryId] = {
            id: hour.project.id,
            entryId, // Including just in case
            color: `#${getProjectColor(hour.project)}`,
            name: hour.project.name,
          };
          return prevMappings;
        },
        {}
      );

      return { ...prev, ...entryMap };
    }, EMPTY_OBJECT);
  }
);

/**
 * Return references to project for the entry ids on Hours and Suggested Hours
 */
export const getMemoryToProjectRelationsForCalendarDay = createSelector(
  [
    getMemoryToProjectRelationsForHoursOnCalendarDate,
    getMemoryToProjectRelationsForSuggestionsOnCalendarDate,
  ],
  (hourMeta, suggestedHourMeta) => {
    // Since all we're doing here is merging the two input objects, we can avoid returning a new
    // object if only one (or none) of the inputs contain any data
    if (!Object.keys(hourMeta).length) {
      return suggestedHourMeta;
    } else if (!Object.keys(suggestedHourMeta).length) {
      return hourMeta;
    }

    return { ...hourMeta, ...suggestedHourMeta };
  }
);

const getMemoriesByHourId = createSelector(
  [getUnprocessedHours, getFilter, getMemoryEntries, getUserId],
  (hours, memoryFilters, memoryEntries, userId) => {
    return hours.reduce<Record<number, Array<Memory>>>((memoryMap, hour) => {
      if (hour.user.id !== userId) {
        // Not a security measure, just for performance in
        // company view etc when there are loaded memories
        return memoryMap;
      }
      memoryMap[hour.id] = memoryEntries
        // Make sure to filter for performance
        .filter(entry => entry.date === hour.day)
        .map(entry => {
          return entryToMemoryFactory(entry, {
            filter: memoryFilters,
            selectedEntryIds: hour.entry_ids,
          });
        });
      return memoryMap;
    }, {});
  }
);

// ! Would it be better to redo what's above to avoid 2 maps, but add the overhead of project loopup?
const getHoursAsCardsCore = createSelector(
  [
    getHours,
    getMemoryGroupsWithProjects,
    getMemoriesByHourId,
    getFilter,
    getPinnedProjectIdsSet,
    getTagEntities,
    getUserId,
    getHasEnabledTic,
    getAccountCurrency,
    getHasHourStatesFeature,
    getUserRoleId,
    getHourStates,
    readReportSelection,
    getUserLedUserIds,
  ],
  (
    hours,
    memoryGroupsSpecifications,
    memoriesByHourId,
    timelineFilter,
    pinnedProjectIdsSet,
    tags,
    userId,
    hasDurationFromTimestamps,
    defaultCurrency,
    hasHourStatesFeature,
    userRoleId,
    states,
    reportSelection,
    userLedUserIds
  ): Array<HourAsCard> => {
    const selectedHourIds = new Set(reportSelection.hourIds);
    return hours
      .map(hour => {
        let memoryGroups: Array<MemoryGroup> = [];
        if (hour.user.id === userId) {
          // Don't process this stuff if there are no memories anyway
          const memories = memoriesByHourId[hour.id];
          memoryGroups = groupMemoriesFactory(memoryGroupsSpecifications, memories, {
            importanceMinThreshold: timelineFilter.importanceMinThreshold,
            expandGroupRefs: timelineFilter.expandGroupRefs,
            expandedEntryIds: timelineFilter.expandedEntryIds,
            activeProjectId: hour.project?.id ?? null,
            operatorMode: OperatorMode.onLoggedHour,
            filterUncheckedMemories: true,
          });
        }
        return hourAsCardFactory(enrichProjectOnHour(hour, pinnedProjectIdsSet), {
          memoryGroups,
          tags,
          hasDurationFromTimestamps,
          defaultCurrency,
          hasHourStatesFeature,
          userRoleId,
          states,
          isSelected: selectedHourIds.has(hour.id),
          isSelectionMode: selectedHourIds.size > 0,
          userLedUserIds,
        });
      })
      .sort((a, b) => a.sequence - b.sequence);
  }
);

// This selector wraps the above and instead selectively changes the currently edited draft, to
// avoid having to re-map all of the hours + memories whenever the hour editor is toggled
export const getHoursAsCards = createSelector(
  [getHoursAsCardsCore, readHourDraftId],
  (hours, draftId) => {
    const currentlyEditedIndex = hours.findIndex(hour => hour.id === draftId);

    if (currentlyEditedIndex === -1) {
      return hours;
    }

    const clonedHours = hours.slice();
    if (currentlyEditedIndex !== -1) {
      const currentlyEditedHour = hours[currentlyEditedIndex];
      clonedHours.splice(currentlyEditedIndex, 1, {
        ...currentlyEditedHour,
        ui: {
          ...currentlyEditedHour.ui,
          isEditing: true,
        },
      });
    }

    return clonedHours;
  }
);

export const getHourCardWithRunningTimer = createSelector(
  [getHoursAsCards],
  hours => hours.find(hour => !!hour.timerRunningSince) || null
);

export interface GetHourTimerStatusType {
  id: number | null;
  day: string | null;
  isTimerRunning: boolean;
  timerCostPerSecond: number | null;
  timerNumLoggedSecondExact: number | null;
  timerRunningSince: number | null;
}

/**
 * ! !
 * Check how often this re-renders; We might have to look at hour's entities directly to avoid
 * expensive re-renders, since this selector is called in CalendarRouteProps selector.
 */
export const getHourTimerStatus = createSelector(
  [getHoursAsCards],
  (hours): GetHourTimerStatusType => {
    const hourWithRunningTimer: Partial<HourAsCard> = hours.find(hour => hour.isTimerRunning) || {};
    return {
      id: hourWithRunningTimer.id || null,
      day: hourWithRunningTimer.day || null,
      isTimerRunning: !!hourWithRunningTimer.isTimerRunning,
      timerCostPerSecond: hourWithRunningTimer.costPerSecond || null,
      timerNumLoggedSecondExact: hourWithRunningTimer.numLoggedSecondsExact || null,
      timerRunningSince: hourWithRunningTimer.timerRunningSince || null,
    };
  }
);

export const getAllHours = createSelector([getHourEntities], entities =>
  Object.values(entities ?? {})
);

export const createHoursForUserSelector = (userId: number) =>
  createSelector([getAllHours], hours =>
    hours.filter(hour => hour.user.id === userId || hour.user_ids.includes(userId))
  );

export const hasLockedEntries = createSelector(
  [getHourEntities, (_: IState, hourIds: Array<number>) => hourIds],
  (hoursById, hourIds): boolean => hourIds.some(id => hoursById[id]?.locked)
);
